import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { PulseHeader } from "./PollCard";
import { pulsesApi } from "../../lib/pulses";
import { formatApiError } from "../../lib/api";

export function MoodCard({ pulse, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [count, setCount] = useState(pulse.payload.vibras_count || 0);
  const ringRef = useRef(null);

  useEffect(() => {
    setCount(pulse.payload.vibras_count || 0);
  }, [pulse.payload.vibras_count]);

  const triggerPing = () => {
    setPinging(true);
    setTimeout(() => setPinging(false), 900);
  };

  const send = async () => {
    if (busy) return;
    setBusy(true);
    // Optimistic ping right away for snappy UX
    triggerPing();
    setCount((c) => c + 1);
    try {
      const data = await pulsesApi.vibra(pulse.id);
      setCount(data.vibras_count);
      onUpdate &&
        onUpdate({
          ...pulse,
          payload: { ...pulse.payload, vibras_count: data.vibras_count },
        });
    } catch (err) {
      setCount((c) => Math.max(0, c - 1));
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="q-glass rounded-3xl p-5 space-y-4 relative overflow-hidden"
      data-testid={`pulse-mood-${pulse.id}`}
    >
      <PulseHeader pulse={pulse} kind="Mood Wave" />

      <div
        ref={ringRef}
        className="relative flex flex-col items-center py-6"
      >
        {pinging && (
          <>
            <span
              className="pointer-events-none absolute inset-0 rounded-full animate-[q-ping_900ms_ease-out]"
              style={{
                background:
                  "radial-gradient(ellipse at center, var(--accent-soft), transparent 60%)",
              }}
              aria-hidden="true"
            />
            <span
              className="pointer-events-none absolute inset-x-1/4 inset-y-2 rounded-full animate-[q-ping_900ms_ease-out_100ms] opacity-70"
              style={{ border: "2px solid var(--accent)" }}
              aria-hidden="true"
            />
          </>
        )}
        <div
          className={
            "relative z-10 font-display text-3xl sm:text-4xl font-bold text-center leading-tight transition-transform " +
            (pinging ? "scale-105" : "scale-100")
          }
          style={{ color: "var(--accent)" }}
          data-testid={`mood-preset-${pulse.id}`}
        >
          {pulse.payload.mood_preset}
        </div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mt-2 font-mono">
          estado activo
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div
          className="flex items-center gap-2 text-sm text-slate-300"
          data-testid={`mood-vibras-count-${pulse.id}`}
        >
          <Zap size={16} className="q-accent-text" />
          <span className="font-mono">{count}</span>
          <span>vibra{count === 1 ? "" : "s"}</span>
        </div>
        <button
          type="button"
          onClick={send}
          disabled={busy}
          data-testid={`mood-send-vibra-${pulse.id}`}
          className={
            "q-btn-primary inline-flex items-center gap-2 transition-transform active:scale-95 " +
            (pinging ? "shadow-[0_0_28px_var(--accent-glow)]" : "")
          }
        >
          <Zap size={16} /> Enviar Vibra
        </button>
      </div>

      <style>{`
        @keyframes q-ping {
          0%   { opacity: 0.9; transform: scale(0.7); }
          60%  { opacity: 0.4; transform: scale(1.4); }
          100% { opacity: 0;   transform: scale(1.9); }
        }
      `}</style>
    </article>
  );
}
